import { Box, Loading } from '@iheartjane/reefer';
import compact from 'lodash-es/compact';
import uniq from 'lodash-es/uniq';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useGetStore } from '@jane/shared/data-access';
/* eslint-disable-next-line no-restricted-imports */
import type { Store, StoreSpecial } from '@jane/shared/models';
import { isStore } from '@jane/shared/util';

import { useCustomerDispatch } from '../../../../../customer/dispatch';
import {
  analyzeSpecials,
  selectCart,
} from '../../../../../customer/redux/cart';
import { selectIdentification } from '../../../../../customer/redux/identification';
import { selectStore } from '../../../../../customer/redux/store';
import { useCustomerSelector } from '../../../../../customer/selectors';
import { isEligibleForGroupDiscount } from '../../../../../lib/userGroupDiscount';
import { LoginProtected } from '../loginProtected';
import { GroupDiscountButton } from './groupDiscountButton';
import { GroupDiscountDetails } from './groupDiscountDetails';
import { GroupDiscountOuter, LoadingContainer } from './groupDiscounts.styles';

export const groupSpecialsTitle = (
  groupSpecials: StoreSpecial[],
  specialLabel?: string | null
) => {
  const applicableGroups = compact(
    uniq(
      groupSpecials.map((s) => {
        if (!s.conditions['qualified_group']) return null;
        const specialType = s.conditions['qualified_group']['type'];
        return specialType === 'pediatric' ? 'pediatric/minors' : specialType;
      })
    )
  )
    .sort()
    .join('/');

  return `Apply ${applicableGroups} ${specialLabel || 'discount'}`;
};

interface Props {
  groupSpecials: StoreSpecial[];
  headless?: boolean;
  userGroupSpecialId?: string | number;
}

export const GroupDiscounts = ({
  groupSpecials,
  headless,
  userGroupSpecialId,
}: Props) => {
  const dispatch = useCustomerDispatch();
  const { document } = useCustomerSelector(selectIdentification);
  const {
    cart: { promo_code: promoCode, reservation_mode: reservationMode },
  } = useCustomerSelector(selectCart);
  const { store: storeState } = useCustomerSelector(selectStore);
  const store = storeState as Store;
  const { time_zone_identifier: timezone, store_compliance_language_settings } =
    store;
  const specialsLabel = store_compliance_language_settings?.['specials'];

  const [selectedSpecialId, setSelectedSpecialId] = useState<
    string | number | undefined
  >(userGroupSpecialId);
  const [eligible, setEligible] = useState<boolean>(false);
  const { analyzingSpecials, cart } = useCustomerSelector(selectCart);
  const { data: fetchedStore } = useGetStore(cart?.store?.id);

  const eligibleRef = useRef(false);
  const firstTimeRef = useRef(true);

  const storeToUse = useMemo(
    () => (isStore(store) ? store : fetchedStore),
    [store, fetchedStore]
  );

  useEffect(() => {
    const eligible = isEligibleForGroupDiscount({
      document,
      special: groupSpecials.find((sp) => sp.id === selectedSpecialId),
      timezone,
      store: storeToUse as Store,
    });
    setEligible(eligible);
    eligibleRef.current = eligible;
  }, [document, selectedSpecialId]);

  useEffect(() => {
    if (firstTimeRef.current) {
      firstTimeRef.current = false;
      return;
    }

    const specialIdToApply =
      eligibleRef.current && selectedSpecialId ? selectedSpecialId : undefined;

    dispatch(
      analyzeSpecials({
        userGroupSpecialId: specialIdToApply,
        reservationMode,
        promoCode,
      })
    );
  }, [selectedSpecialId, eligible]);

  const selectedDiscount = groupSpecials.find(
    (d) => selectedSpecialId === d.id
  );

  return (
    <Box position="relative" pl={headless ? undefined : 40}>
      <LoginProtected specialLabel={specialsLabel}>
        <>
          {analyzingSpecials && (
            <LoadingContainer
              alignItems="center"
              flexDirection="column"
              mx="auto"
            >
              <Loading />
            </LoadingContainer>
          )}
          <GroupDiscountOuter>
            <GroupDiscountButton
              analyzingSpecials={analyzingSpecials}
              groupDiscounts={groupSpecials}
              selectSpecial={setSelectedSpecialId}
              selectedSpecial={selectedSpecialId}
            />
            {selectedDiscount && (
              <GroupDiscountDetails
                special={selectedDiscount}
                timezone={timezone}
                store={storeToUse}
              />
            )}
          </GroupDiscountOuter>
        </>
      </LoginProtected>
    </Box>
  );
};
